import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BarChart3, Loader, AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { useAnalytics } from '../hooks/useAnalytics';
import EventFinderAPI from '../services/api';
import { EventCategory, EventCategoriesResponse } from '../types/api';
import SEO, { createBreadcrumbStructuredData } from '../components/SEO';

const CategoriesPage = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const Header = styled.section`
  background: ${colors.gradients.hero};
  color: ${colors.white};
  padding: ${spacing[12]} 0 ${spacing[8]};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[8]} 0 ${spacing[6]};
  }
`;

const HeaderContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  position: relative;
  z-index: 1;
  
  @media (min-width: ${breakpoints.sm}) {
    padding: 0 ${spacing[6]};
  }
`;

const Title = styled.h1`
  font-size: ${typography.fontSizes['4xl']};
  font-weight: ${typography.fontWeights.bold};
  margin-bottom: ${spacing[4]};
  line-height: ${typography.lineHeights.tight};
  color: ${colors.white};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['3xl']};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const Description = styled.p`
  font-size: ${typography.fontSizes.lg};
  margin-bottom: ${spacing[8]};
  opacity: 0.95;
  color: ${colors.white};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.base};
    margin-bottom: ${spacing[6]};
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing[8]} ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[8]} ${spacing[6]};
  }
`;



const StatsBar = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[4]};
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const StatIcon = styled.div`
  color: ${colors.primary[500]};
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${spacing[6]};
  
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[4]};
  }
`;

const CategoryCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  box-shadow: ${shadows.sm};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
    border-color: ${colors.primary[300]};
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  margin-bottom: ${spacing[4]};
  gap: ${spacing[3]};
`;

const CategoryName = styled.h3`
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0;
  flex: 1;
  line-height: ${typography.lineHeights.tight};
`;

const CategoryBadge = styled.div`
  background: ${colors.primary[100]};
  color: ${colors.primary[700]};
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  white-space: nowrap;
`;

const CategoryDescription = styled.p`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  margin-bottom: ${spacing[4]};
  line-height: ${typography.lineHeights.relaxed};
`;

const CategoryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
`;

const StatCard = styled.div`
  background: ${colors.background.secondary};
  padding: ${spacing[3]};
  border-radius: ${borderRadius.md};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[12]};
  color: ${colors.text.secondary};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing[12]};
  color: ${colors.text.secondary};
  text-align: center;
`;

const ErrorMessage = styled.p`
  margin-top: ${spacing[4]};
  font-size: ${typography.fontSizes.base};
`;

const RetryButton = styled.button`
  margin-top: ${spacing[4]};
  padding: ${spacing[3]} ${spacing[6]};
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: ${colors.primary[600]};
  }
`;

const EventCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCategories, setTotalCategories] = useState(0);

  const loadCategories = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response: EventCategoriesResponse = await EventFinderAPI.getEventCategories(
        {}, // no filters
        20, // limit
        false // includeExamples - disabled for performance
      );
      
      setCategories(response.categories);
      setTotalCategories(response.totalCategories);
      
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  // Track analytics when categories load
  React.useEffect(() => {
    if (categories.length > 0 && totalCategories > 0) {
      trackEvent({
        action: 'categories_loaded',
        category: 'event_categories',
        label: `${totalCategories} categories`,
        value: totalCategories,
      });
    }
  }, [categories.length, totalCategories, trackEvent]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleCategoryClick = (category: EventCategory) => {
    // Navigate to browse page with category filter using eventType parameter
    navigate(`/browse?eventType=${encodeURIComponent(category.name)}`);
    trackEvent({
      action: 'category_clicked',
      category: 'event_categories',
      label: category.name,
      value: category.eventCount,
      custom_parameters: {
        category_name: category.name,
        event_count: category.eventCount,
      },
    });
  };

  const totalEvents = categories.reduce((sum, cat) => sum + cat.eventCount, 0);
  const totalUpcomingEvents = categories.reduce((sum, cat) => sum + cat.upcomingEventCount, 0);

  if (loading) {
    return (
      <CategoriesPage>
        <LoadingContainer>
          <Loader size={32} className="animate-spin" />
          <p style={{ marginTop: spacing[4] }}>Loading event categories...</p>
        </LoadingContainer>
      </CategoriesPage>
    );
  }

  if (error) {
    return (
      <CategoriesPage>
        <ErrorContainer>
          <AlertCircle size={48} color={colors.error[500]} />
          <ErrorMessage>Error loading categories: {error}</ErrorMessage>
          <RetryButton onClick={loadCategories}>
            Try Again
          </RetryButton>
        </ErrorContainer>
      </CategoriesPage>
    );
  }

  return (
    <CategoriesPage>
      <SEO
        title="Event Categories - Browse Events by Type | Event Finder"
        description="Explore events by category using AI-powered semantic search. Discover conferences, workshops, and events across different industries and topics."
        keywords="event categories, event types, conferences, workshops, webinars, networking events, training sessions, tech events"
        canonical="https://www.eventfinder.site/categories"
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: 'https://www.eventfinder.site/' },
            { name: 'Event Categories', url: 'https://www.eventfinder.site/categories' }
          ]),
          ...(categories.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Event Categories",
            "description": "Browse events by category on Event Finder",
            "url": "https://www.eventfinder.site/categories",
            "numberOfItems": totalCategories,
            "hasPart": categories.slice(0, 10).map(cat => ({
              "@type": "ItemList",
              "name": cat.name,
              "description": cat.description || `${cat.eventCount} events in ${cat.name}`,
              "numberOfItems": cat.eventCount
            }))
          }] : [])
        ]}
      />
      <Header>
        <HeaderContent>
          <Title>Event Categories</Title>
          <Description>
            Explore events by category using AI-powered semantic search. 
            Discover conferences, workshops, and events across different industries and topics.
          </Description>
        </HeaderContent>
      </Header>

      <Container>
        <StatsBar>
          <StatItem>
            <StatIcon><BarChart3 size={16} /></StatIcon>
            <span>{totalCategories} Categories</span>
          </StatItem>
          <StatItem>
            <StatIcon><Calendar size={16} /></StatIcon>
            <span>{totalEvents.toLocaleString()} Total Events</span>
          </StatItem>
          <StatItem>
            <StatIcon><TrendingUp size={16} /></StatIcon>
            <span>{totalUpcomingEvents.toLocaleString()} Upcoming Events</span>
          </StatItem>
        </StatsBar>

        <CategoriesGrid>
          {categories.map((category) => (
            <CategoryCard
              key={category.name}
              onClick={() => handleCategoryClick(category)}
              role="button"
              tabIndex={0}
              aria-label={`${category.name} - ${category.eventCount} events, ${category.upcomingEventCount} upcoming`}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCategoryClick(category); } }}
            >
              <CategoryHeader>
                <CategoryName>{category.name}</CategoryName>
                <CategoryBadge>{category.eventCount}</CategoryBadge>
              </CategoryHeader>
              
              {category.description && (
                <CategoryDescription>{category.description}</CategoryDescription>
              )}
              
              <CategoryStats>
                <StatCard>
                  <StatValue>{category.eventCount}</StatValue>
                  <StatLabel>Total Events</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{category.upcomingEventCount}</StatValue>
                  <StatLabel>Upcoming</StatLabel>
                </StatCard>
              </CategoryStats>
            </CategoryCard>
          ))}
        </CategoriesGrid>
        
        {categories.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: spacing[12], color: colors.text.secondary }}>
            <BarChart3 size={48} style={{ marginBottom: spacing[4] }} />
            <p>No categories found.</p>
            <p>Please try again later.</p>
          </div>
        )}
      </Container>
    </CategoriesPage>
  );
};

export default EventCategoriesPage;